import { useEffect, useState } from "react";
import { Plus, Search, Edit2, Trash2, ChevronDown, MapPin } from "lucide-react";
import { getSites, createSite, updateSite, deleteSite, getClients } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";

export default function Sites() {
  const [sites, setSites] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    client_id: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    contact_name: "",
    contact_phone: "",
    notes: "",
  });

  useEffect(() => {
    loadData();
    // `loadData` is a stable closure that reads the latest filter
    // state via React's render snapshot — adding it to deps would
    // require wrapping in useCallback with no behavior change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, clientFilter]);

  const loadData = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (clientFilter !== "all") params.client_id = clientFilter;
      
      const [sitesRes, clientsRes] = await Promise.all([
        getSites(params),
        getClients()
      ]);
      setSites(sitesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSite) {
        await updateSite(editingSite.id, formData);
        toast.success("Site updated successfully");
      } else {
        await createSite(formData);
        toast.success("Site created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error("Failed to save site");
    }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      client_id: site.client_id,
      address: site.address,
      city: site.city || "",
      state: site.state || "",
      zip_code: site.zip_code || "",
      contact_name: site.contact_name || "",
      contact_phone: site.contact_phone || "",
      notes: site.notes || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this site?")) {
      try {
        await deleteSite(id);
        toast.success("Site deleted successfully");
        loadData();
      } catch (error) {
        toast.error("Failed to delete site");
      }
    }
  };

  const resetForm = () => {
    setEditingSite(null);
    setFormData({
      name: "",
      client_id: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      contact_name: "",
      contact_phone: "",
      notes: "",
    });
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : "-";
  };

  return (
    <div data-testid="sites-page">
      <div className="data-table-container">
        <div className="data-table-header">
          <div className="data-table-title">
            <MapPin className="w-5 h-5 inline mr-2 text-slate-500" />
            Site Locations
          </div>

          <div className="flex items-center gap-3">
            <div className="search-input-wrapper">
              <Search />
              <Input
                placeholder="Search sites..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input w-64"
                data-testid="search-sites"
              />
            </div>

            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-48" data-testid="filter-client">
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={resetForm} data-testid="btn-add-site">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Site
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {editingSite ? "Edit Site" : "Add New Site"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="form-grid">
                    <div>
                      <Label htmlFor="name">Site Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_id">Client *</Label>
                      <Select
                        value={formData.client_id}
                        onValueChange={(value) => setFormData({ ...formData, client_id: value })}
                      >
                        <SelectTrigger data-testid="select-client">
                          <SelectValue placeholder="Select client" />
                        </SelectTrigger>
                        <SelectContent>
                          {clients.map((client) => (
                            <SelectItem key={client.id} value={client.id}>
                              {client.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="form-grid-full">
                      <Label htmlFor="address">Address *</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        required
                        data-testid="input-address"
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        data-testid="input-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={formData.state}
                        onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                        data-testid="input-state"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zip_code">ZIP Code</Label>
                      <Input
                        id="zip_code"
                        value={formData.zip_code}
                        onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                        data-testid="input-zip"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_name">Site Contact Name</Label>
                      <Input
                        id="contact_name"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        data-testid="input-contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact_phone">Site Contact Phone</Label>
                      <Input
                        id="contact_phone"
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        data-testid="input-contact-phone"
                      />
                    </div>
                    <div className="form-grid-full">
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        rows={3}
                        data-testid="input-notes"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="btn-save-site">
                      {editingSite ? "Update" : "Create"} Site
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Site Name</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto"></div>
                </TableCell>
              </TableRow>
            ) : sites.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  <div className="empty-state">
                    <MapPin className="empty-state-icon" />
                    <p className="empty-state-title">No sites found</p>
                    <p className="empty-state-text">Add your first site to get started</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sites.map((site) => (
                <TableRow key={site.id} className="table-row-hover" data-testid={`site-row-${site.id}`}>
                  <TableCell className="font-medium">{site.name}</TableCell>
                  <TableCell>{getClientName(site.client_id)}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm text-slate-600">
                    {site.address}
                  </TableCell>
                  <TableCell>
                    {site.city || site.state ? (
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <MapPin className="w-3 h-3" />
                        {[site.city, site.state].filter(Boolean).join(", ")}
                      </div>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="text-sm">{site.contact_name || "-"}</div>
                      <div className="text-xs text-slate-500">{site.contact_phone || ""}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {format(new Date(site.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`site-actions-${site.id}`}>
                          Actions
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(site)}>
                          <Edit2 className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(site.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
